import { Component } from '@theme/component';

const reviewFormStyles = new Map();
const reviewFormScripts = new Map();
const failedReviewFormScripts = new Set();

function prepareReviewFormScript(path, ready) {
  if (ready()) return Promise.resolve();
  if (reviewFormScripts.has(path)) return reviewFormScripts.get(path);
  const promise = new Promise((resolve, reject) => {
    let poll;
    const started = Date.now();
    const check = () => {
      clearTimeout(poll);
      if (ready()) {
        failedReviewFormScripts.delete(path);
        resolve();
      } else if (Date.now() - started >= 10000) {
        reject(new Error('Review form script did not become ready'));
      } else poll = setTimeout(check, 50);
    };
    try {
      // The vendor skips duplicate requests, so also observe its real exports.
      // A failed request can be explicitly retried on the customer's next click.
      window.jdgm.loadScript(path, check, failedReviewFormScripts.has(path));
      check();
    } catch (error) {
      clearTimeout(poll);
      reject(error);
    }
  }).catch(error => {
    reviewFormScripts.delete(path);
    failedReviewFormScripts.add(path);
    throw error;
  });
  reviewFormScripts.set(path, promise);
  return promise;
}

async function prepareReviewFormRuntime() {
  const vendor = window.jdgm;
  if (typeof vendor?.loadScript !== 'function') throw new Error('Review loader is unavailable');
  await prepareReviewFormScript('widget/base.js', () => typeof vendor.$ === 'function');
  await prepareReviewFormScript('widget/common.js', () => typeof vendor.customizeReviews === 'function');
  await prepareReviewFormScript('widget/write_review_modal.js', () => typeof vendor._WriteReviewModal === 'function');
  await prepareReviewFormScript('widget/form.js', () =>
    typeof vendor._rtlStarsEnabled === 'function' &&
    typeof vendor._applyRtlStarsOrder === 'function' &&
    typeof vendor.setupReviewFormValidation === 'function');
}

// The installed widget opens its form before its lazy CSS has finished loading.
// Load that same stylesheet first so the vendor's first click has a fixed modal.
function prepareReviewFormStyles() {
  const loader = [...document.scripts].find(script => {
    if (!script.src) return false;
    const url = new URL(script.src);
    return url.hostname === 'cdn.shopify.com' && /\/judgeme-[^/]+\/assets\/loader\.js$/.test(url.pathname);
  });
  if (!loader) return Promise.reject(new Error('Installed review loader is unavailable'));
  const href = new URL('widget_v3_form.css', loader.src).href;
  if (reviewFormStyles.has(href)) return reviewFormStyles.get(href);
  const promise = new Promise((resolve, reject) => {
    const existing = [...document.querySelectorAll('link[rel="stylesheet"]')].find(link => link.href === href && !link.dataset.dlReviewStyleFailed);
    if (existing?.sheet) return resolve();
    const link = existing || document.createElement('link');
    const finish = error => {
      clearTimeout(timeout);
      link.removeEventListener('load', loaded);
      link.removeEventListener('error', failed);
      if (error) {
        link.dataset.dlReviewStyleFailed = 'true';
        if (!existing) link.remove();
        reject(error);
      } else resolve();
    };
    const loaded = () => finish();
    const failed = () => finish(new Error('Review form stylesheet could not load'));
    const timeout = setTimeout(failed, 12000);
    link.addEventListener('load', loaded, { once: true });
    link.addEventListener('error', failed, { once: true });
    if (!existing) {
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.dlReviewFormStyle = '';
      document.head.append(link);
    }
  }).catch(error => {
    reviewFormStyles.delete(href);
    throw error;
  });
  reviewFormStyles.set(href, promise);
  return promise;
}

class DlCatalog extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-dl-open]');
      if (trigger) {
        const dialog = document.getElementById(trigger.dataset.dlOpen);
        if (dialog instanceof HTMLDialogElement) {
          this.dialogTrigger = trigger;
          dialog.showModal();
        }
      }
      if (event.target instanceof HTMLDialogElement) {
        const rect = event.target.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) event.target.close();
      }
    }, { signal });
    for (const dialog of this.querySelectorAll('dialog')) {
      dialog.addEventListener('close', () => this.dialogTrigger?.focus(), { signal });
    }
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-dl-sort]')) event.target.form.requestSubmit();
    }, { signal });
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('.dl-collection-layout')) return;
      const form = event.target;
      for (const extra of form.querySelectorAll('[data-dl-query]')) extra.remove();
      const current = new URL(window.location.href);
      for (const [name, value] of current.searchParams) {
        if (name === 'page' || name === 'sort_by' || name.startsWith('filter.')) continue;
        const hidden = document.createElement('input');
        hidden.type = 'hidden';
        hidden.name = name;
        hidden.value = value;
        hidden.dataset.dlQuery = '';
        form.append(hidden);
      }
      for (const field of form.querySelectorAll('input[type="number"]')) field.disabled = field.value === '';
    }, { signal });
    this.mobileQuery = window.matchMedia('(max-width: 749px)');
    this.updateFilterPanel();
    this.mobileQuery.addEventListener('change', () => this.updateFilterPanel(), { signal });
  }

  updateFilterPanel() {
    const panel = this.querySelector('.dl-filter-panel');
    if (panel) panel.open = !this.mobileQuery.matches;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
  }
}

class DlProduct extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.variantData = JSON.parse(this.querySelector('[data-dl-variant-data]')?.textContent || '{}');
    this.enhance();
    this.observeReviewCloseButton();
    this.prepareReviewForm();
    this.addEventListener('click', event => {
      const trigger = event.target.closest('[data-testid="write-review-button"], .jdgm-write-rev-link');
      if (!trigger || this.reviewFormReady) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.openReviewForm(trigger);
    }, { signal, capture: true });
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-dl-variant]')) this.updateVariant();
      if (event.target.matches('[data-dl-quantity]') && event.target.checkValidity()) {
        const pairQuantity = this.querySelector('[data-dl-pair-main-quantity]');
        if (pairQuantity) pairQuantity.value = event.target.value;
        this.updatePairTotal();
      }
      if (event.target.matches('[data-dl-pair-toggle]')) {
        const item = event.target.closest('.dl-pair-item');
        for (const input of item.querySelectorAll('[data-dl-pair-main], [data-dl-pair-main-quantity], [data-dl-pair-id], [data-dl-pair-quantity]')) input.disabled = !event.target.checked;
        this.updatePairTotal();
      }
    }, { signal });
    this.addEventListener('submit', (event) => {
      if (!event.target.matches('[data-dl-pair-form]')) return;
      const mainQuantity = this.querySelector('[data-dl-quantity]');
      const pairQuantity = this.querySelector('[data-dl-pair-main-quantity]');
      if (mainQuantity && pairQuantity && !pairQuantity.disabled) {
        if (!mainQuantity.reportValidity()) {
          event.preventDefault();
          return;
        }
        pairQuantity.value = mainQuantity.value;
      }
      let itemIndex = 0;
      for (const item of event.target.querySelectorAll('.dl-pair-item')) {
        const id = item.querySelector('[data-dl-pair-main], [data-dl-pair-id]');
        const quantity = item.querySelector('[data-dl-pair-main-quantity], [data-dl-pair-quantity]');
        if (!id || !quantity || id.disabled) continue;
        id.name = `items[${itemIndex}][id]`;
        quantity.name = `items[${itemIndex}][quantity]`;
        itemIndex += 1;
      }
      if (itemIndex === 0) event.preventDefault();
    }, { signal });
    this.addEventListener('click', (event) => {
      const variantButton = event.target.closest('[data-dl-variant-button]');
      if (variantButton) {
        this.querySelector('[data-dl-variant]').value = variantButton.dataset.dlVariantButton;
        this.querySelector('[data-dl-variant]').dispatchEvent(new Event('change', { bubbles: true }));
      }
      const quantityButton = event.target.closest('[data-dl-quantity-change]');
      if (quantityButton) {
        const input = this.querySelector('[data-dl-quantity]');
        if (Number(quantityButton.dataset.dlQuantityChange) > 0) input.stepUp();
        else input.stepDown();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }, { signal });
  }

  updatedCallback() {
    super.updatedCallback();
    this.variantData = JSON.parse(this.querySelector('[data-dl-variant-data]')?.textContent || '{}');
    this.enhance();
    this.prepareReviewForm();
  }

  prepareReviewForm() {
    if (!this.querySelector('.jdgm-review-widget')) return;
    this.reviewFormStylePromise ||= Promise.all([prepareReviewFormStyles(), prepareReviewFormRuntime()]).then(() => {
      this.reviewFormReady = true;
      return true;
    }).catch(() => {
      this.reviewFormStylePromise = null;
      return false;
    });
  }

  observeReviewCloseButton() {
    this.reviewCloseObserver?.disconnect();
    const observed = new WeakSet();
    const restore = () => {
      for (const button of document.querySelectorAll('.jdgm-write-review-modal__content > .jdgm-write-review-modal__close-btn')) {
        if (!observed.has(button)) {
          observed.add(button);
          this.reviewCloseObserver.observe(button, { attributes: true, attributeFilter: ['tabindex', 'aria-hidden'] });
        }
        // Keep the vendor's existing close action available at every form step.
        if (button.getAttribute('tabindex') === '-1') button.removeAttribute('tabindex');
        if (button.getAttribute('aria-hidden') === 'true') button.removeAttribute('aria-hidden');
      }
    };
    this.reviewCloseObserver = new MutationObserver(restore);
    this.reviewCloseObserver.observe(document.body, { childList: true });
    restore();
  }

  async openReviewForm(trigger) {
    if (this.reviewFormOpening) return;
    this.reviewFormOpening = true;
    trigger.setAttribute('aria-busy', 'true');
    const reviews = this.querySelector('.dl-product-reviews');
    let status = reviews.querySelector('[data-dl-review-status]');
    if (status) status.hidden = true;
    this.prepareReviewForm();
    const ready = await this.reviewFormStylePromise;
    this.reviewFormOpening = false;
    trigger.removeAttribute('aria-busy');
    if (!this.isConnected || !trigger.isConnected) return;
    if (ready) {
      trigger.click();
      return;
    }
    if (!status) {
      status = document.createElement('p');
      status.dataset.dlReviewStatus = '';
      status.className = 'dl-review-load-status';
      status.setAttribute('role', 'alert');
      reviews.prepend(status);
    }
    status.textContent = this.variantData.reviewLoadError;
    status.hidden = false;
  }

  enhance() {
    for (const control of this.querySelectorAll('[data-dl-quantity-change], [data-dl-pair-toggle]')) control.hidden = false;
    const pills = this.querySelector('[data-dl-pills]');
    if (pills) {
      pills.hidden = false;
      this.querySelector('[data-dl-variant]').hidden = true;
    }
    this.updatePairTotal();
  }

  updatePairTotal() {
    const total = this.querySelector('[data-dl-pair-total]');
    if (!total) return;
    let cents = 0;
    let selected = 0;
    let available = true;
    const variantId = this.querySelector('[data-dl-variant]')?.value;
    const variant = this.variantData.variants?.find(item => String(item.id) === variantId);
    for (const item of this.querySelectorAll('.dl-pair-item')) {
      const checkbox = item.querySelector('[data-dl-pair-toggle]');
      if (checkbox && !checkbox.checked) continue;
      const quantity = item.querySelector('[data-dl-pair-main-quantity], [data-dl-pair-quantity]');
      const count = Number(quantity?.value || 1);
      cents += Number(item.dataset.dlPairUnitPrice || 0) * count;
      selected += 1;
      if (item.querySelector('[data-dl-pair-main]') && !variant?.available) available = false;
    }
    total.textContent = new Intl.NumberFormat(this.variantData.locale || document.documentElement.lang || 'en', {
      style: 'currency', currency: this.variantData.currency || 'USD',
    }).format(cents / 100);
    const submit = this.querySelector('[data-dl-pair-submit]');
    if (submit) submit.disabled = selected === 0 || !available;
  }

  updateVariant() {
    const select = this.querySelector('[data-dl-variant]');
    const variant = this.variantData.variants?.find((item) => String(item.id) === select.value);
    if (!variant) return;
    this.querySelector('[data-dl-price]').textContent = variant.price;
    const compare = this.querySelector('[data-dl-compare]');
    compare.textContent = variant.compare;
    compare.hidden = !variant.onSale;
    const saving = this.querySelector('[data-dl-saving]');
    saving.textContent = variant.saving;
    saving.hidden = !variant.onSale;
    const addButton = this.querySelector('[data-dl-add]');
    addButton.disabled = !variant.available;
    addButton.textContent = variant.available ? this.variantData.addLabel : this.variantData.soldLabel;
    const payment = this.querySelector('[data-dl-payment]');
    if (payment) payment.hidden = !variant.available;
    const quantity = this.querySelector('[data-dl-quantity]');
    quantity.min = variant.minimum;
    quantity.step = variant.increment;
    if (variant.maximum != null) quantity.max = variant.maximum;
    else quantity.removeAttribute('max');
    quantity.value = variant.minimum;
    for (const pill of this.querySelectorAll('[data-dl-variant-button]')) pill.setAttribute('aria-pressed', String(pill.dataset.dlVariantButton === select.value));
    const pairMain = this.querySelector('[data-dl-pair-main]');
    if (pairMain) {
      pairMain.value = variant.id;
      pairMain.closest('.dl-pair-item').dataset.dlPairUnitPrice = String(variant.priceCents);
      this.querySelector('[data-dl-pair-main-quantity]').value = variant.minimum;
      this.querySelector('[data-dl-pair-main-price]').textContent = variant.price;
      this.updatePairTotal();
    }
    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState(window.history.state, '', url);
    for (const media of this.querySelectorAll('[data-dl-media]')) media.classList.toggle('dl-product-media--selected', media.dataset.dlMedia === String(variant.mediaId));
    select.dispatchEvent(new Event('input', { bubbles: true }));
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.controller?.abort();
    this.reviewCloseObserver?.disconnect();
  }
}

if (!customElements.get('dl-catalog')) customElements.define('dl-catalog', DlCatalog);
if (!customElements.get('dl-product')) customElements.define('dl-product', DlProduct);
